import React from 'react';
import { useParams } from 'react-router-dom';
import { Table, Tag } from '@exxonmobil/react-unity';
import styled from 'styled-components';
import { isString } from 'lodash';
import { ErrorBox } from '../../common/components/ErrorBox';
import { StyledTable, TableBodyRow, TableCell, TableHeadRow } from '../../common/components/TableComponents';
import { DataTableLoading } from '../../common/components/TableLoading';
import { Endpoint, EndpointType } from '../../common/types/EndpointType';

interface Props {
  data: EndpointType[];
  isLoading: boolean;
  error: string;
  titles: string[];
  onRefresh: () => void;
}

const DataHeaders = ({ titles }: { titles: string[] }) => (
  <Table.Head>
    <TableHeadRow style={{ height: '2px' }}>
      {titles.map((title, idx) => (
        <Table.Head.Cell key={idx}>{title}</Table.Head.Cell>
      ))}
    </TableHeadRow>
  </Table.Head>
);

const DataRows = ({ endpoints: { endpoints } }: { endpoints: { endpoints: Endpoint[] } }) => (
  <Table.Body>
    {endpoints.map((endpoint) => (
      <TableBodyRow key={endpoint.id}>
        <TableCell>{endpoint.description}</TableCell>
        {/* <TableCell>{endpoint.service}</TableCell> */}
        <TableCell>{endpoint.type}</TableCell>
        {/* <TableCell>{endpoint.method}</TableCell> */}
        {/* <TableCell>{endpoint.endpoint}</TableCell> */}
        <SourceTableCell sources={endpoint.sources} />
        <TableCell>{endpoint.numberOfConsumer}</TableCell>
        <ConsumerTableCell values={endpoint.consumers.map((consumer) => consumer.name)} />
        <ConsumerTableCell values={endpoint.consumers.map((consumer) => consumer.volume)} />
      </TableBodyRow>
    ))}
  </Table.Body>
);

const getTableContent = (error: string, onRefresh: () => void, isLoading: boolean, data: EndpointType[]) => {
  const { id } = useParams();

  if (error) {
    if (error === '401' || error === '403') {
      return <EmptyDataContainer>Sessions timeout, please close the page and login again.</EmptyDataContainer>;
    }
    return <ErrorBox onRefresh={onRefresh} isRefreshable />;
  }
  if (isLoading) {
    return <DataTableLoading rowNumber={5} columnSpan={1} />;
  }
  if (data.length === 0) {
    return <EmptyDataContainer>No data available in table</EmptyDataContainer>;
  }
  return data.map((d) => {
    if (d.id.toString() === id) {
      return <DataRows key={d.id} endpoints={d} />;
    }
    return null;
  });
};

const UtilizationTable = ({ data, isLoading, error, titles, onRefresh }: Props) => (
  <StyledTable striped>
    <DataHeaders titles={titles} />
    {getTableContent(error, onRefresh, isLoading, data)}
  </StyledTable>
);

const EmptyDataContainer = styled.div`
  text-align: center;
  padding-top: 2rem;
  min-height: 5rem;
`;

const SourceTableCell = ({ sources }: { sources: string[] }) => {
  return (
    <TableCell>
      <Tag.Group>
        {sources.map((source) => (
          <Tag key={source} className="em-c-tags--light">
            {source}
          </Tag>
        ))}
      </Tag.Group>
    </TableCell>
  );
};

const ConsumerTableCell = ({ values }: any) => {
  const toRender = values as string[] | number[];

  const totalRow = () => {
    if (typeof values[0] === 'number') {
      return <b>{values.reduce((partialSum: number, a: number) => partialSum + a, 0)}</b>;
    }
    if (isString(values[0])) {
      return <b>total</b>;
    }
    return null;
  };

  return (
    <TableCell>
      <StyledConsumerCell>
        {toRender.map((value) => {
          return <p style={{ lineHeight: '1' }}>{value}</p>;
        })}
      </StyledConsumerCell>
      <StyledConsumerCell>{totalRow()}</StyledConsumerCell>
    </TableCell>
  );
};

const StyledConsumerCell = styled.p`
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  margin-right: 8px;
  margin-bottom: 0;
`;

export default UtilizationTable;
